/**
 * Layout Tree helpers — Elementor-inspired hierarchical operations on
 * the flat homepage `sections[]` collection (plus the Visual Builder's
 * own `sections[]` array).
 *
 * We deliberately DO NOT introduce a nested Section→Row→Column→Widget
 * tree schema at this stage because migrating every legacy consumer
 * (Home.jsx renderer, mobile app, POS) to render a nested tree is a
 * high-risk multi-week change. Instead these helpers give the super
 * admin CRUD parity with Elementor — add / duplicate / delete / move /
 * hide / lock / rename — on the flat schema that consumers already
 * understand. A future migration can lift this into a nested tree
 * without touching the callers of this module.
 *
 * All functions are pure and immutable — they return new arrays.
 * Business logic (price/stock/cart) is never accessed here.
 */

let seq = 0;
const nid = (prefix = "sec") => {
  seq += 1;
  return `${prefix}-${Date.now().toString(36)}-${seq.toString(36)}`;
};

const clone = (v) => (v == null ? v : JSON.parse(JSON.stringify(v)));

/**
 * Move item at index `from` to index `to` in a new array.
 */
export function moveItem(list, from, to) {
  const next = Array.isArray(list) ? [...list] : [];
  if (from < 0 || from >= next.length) return next;
  const bounded = Math.max(0, Math.min(next.length - 1, to));
  const [x] = next.splice(from, 1);
  next.splice(bounded, 0, x);
  return next;
}

/**
 * Duplicate item at index `idx`. A fresh id/key is assigned so the two
 * entries render side-by-side without conflicting keys.
 */
export function duplicateItem(list, idx, { keyField = "id", labelField = "label" } = {}) {
  const next = Array.isArray(list) ? [...list] : [];
  const src = next[idx];
  if (!src) return next;
  const copy = clone(src);
  copy[keyField] = nid(String(src[keyField] || "node"));
  if (copy[labelField]) copy[labelField] = `${copy[labelField]} (copy)`;
  next.splice(idx + 1, 0, copy);
  return next;
}

export function removeItem(list, idx) {
  const next = Array.isArray(list) ? [...list] : [];
  if (idx < 0 || idx >= next.length) return next;
  next.splice(idx, 1);
  return next;
}

export function updateItem(list, idx, patch) {
  const next = Array.isArray(list) ? [...list] : [];
  if (idx < 0 || idx >= next.length) return next;
  next[idx] = { ...next[idx], ...patch };
  return next;
}

/**
 * Add a fresh section entry. If `template` is provided, all its keys
 * are copied except the id/key — used by "Add from library".
 */
export function addSection(list, comp, template = null) {
  const next = Array.isArray(list) ? [...list] : [];
  const base = template ? clone(template) : {};
  base.id = nid("s");
  base.comp = comp || base.comp || "text";
  next.push(base);
  return next;
}

/**
 * Add a homepage section (customer-facing runtime section with `key`).
 * Preserves the existing shape used by Home.jsx.
 */
export function addHomepageSection(list, key, label) {
  const next = Array.isArray(list) ? [...list] : [];
  next.push({
    key: `${key}-${nid("h").split("-")[1]}`,
    label: label || key,
    enabled: true,
    title_override: "",
    subtitle: "",
    bg: "",
    padding: "md",
    per_row_mobile: 0,
    per_row_desktop: 0,
    limit: 0,
    filter_categories: [],
    filter_brands: [],
    _base_key: key, // remember original registry key so consumer maps to the right renderer
  });
  return next;
}

/**
 * Resolve the effective renderer key for a homepage section — always
 * returns something Home.jsx's registry knows. Falls back to the raw
 * `key` when no `_base_key` alias is present (legacy sections).
 */
export function homepageSectionKey(section) {
  return section?._base_key || section?.key;
}
